import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CardsColumn,
  CardSlot,
  CardSlotInner,
  DotButton,
  DotColumn,
  NavigationWrapper
} from "./ActivityDotNavigation.styles";
import type { ActivityDotNavigationProps, ActivityItem } from "./ActivityDotNavigation.types";
import { CourseActivityCard } from "./CourseActivityCard.component";
import { useNavigate } from "../../services";

export function ActivityDotNavigation({
  activities,
  initialActiveLoId,
  courseId
}: ActivityDotNavigationProps) {

  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(() => {
    if (initialActiveLoId != null) {
      const initialIndex = activities?.findIndex((activity) => activity.id === initialActiveLoId) ?? -1;
      if (initialIndex >= 0) return initialIndex;
    }
    return 0;
  });
  const [advancedNavigation, setAdvancedNavigation] = useState<boolean>(false);

  const [indexFirstDot, setIndexFirstDot] = useState(0);

  const maxActivitiesVisible = 8;

  const maxStart = activities.length - maxActivitiesVisible;
  const indexesDotList = [
    indexFirstDot - 1,
    ...Array.from({ length: maxActivitiesVisible }, (_, k) => indexFirstDot + k),
    indexFirstDot + maxActivitiesVisible
  ];

  const pendingActivationRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pendingActivationRef.current) clearTimeout(pendingActivationRef.current);
    };
  }, []);

  const dots = () => {
    if(!advancedNavigation) { 
      return activities.map((activity, i) => {
        return {
          ...activity,
          index: i,
          small: false,
          active: i === activeIndex,
          onClick: () => {
            handleDotClick(i);
          }
        };
      });
    }
    return indexesDotList
      .filter(i => i >= 0 && i <= activities.length - 1)
      .map(i => {
        const isShrunk = i === indexFirstDot - 1 || i === indexFirstDot + maxActivitiesVisible;
  
        return {
          index: i,
          small: isShrunk,
          active: i === activeIndex,
          onClick: () => {
            syncUrlToActivity(activities?.[i]);
            setActiveIndex(i);
            if (i === indexFirstDot + maxActivitiesVisible) {
              setIndexFirstDot(s => Math.min(s + 1, maxStart));
              return;
            } 
            if (i === indexFirstDot - 1) {
              setIndexFirstDot(s => Math.max(s - 1, 0));
              return;
            } 
          }
        };
      });
  }; 

  const syncUrlToActivity = useCallback((activity?: ActivityItem) => {
    if (!activity?.id || !activity.learningObjectTypology) return;
    const detailLink = navigate.getDetailLink({
      courseId: activity.courseId ?? activity.parentId ?? undefined,
      id: activity.id,
      isForYou: window.location.href.includes("per-te"),
      learningObjectTypology: activity.learningObjectTypology,
      rootId: activity.rootId ?? undefined
    });
    if (window.location.pathname === detailLink) return;
    navigate(detailLink, { replace: true, state: { preserveScroll: true } });
  }, [navigate]);

  const handleDotClick = useCallback((index: number) => {
    setActiveIndex(index);
    syncUrlToActivity(activities?.[index]);
  }, [activities, syncUrlToActivity]);

  
  useEffect(() => {
    syncUrlToActivity(activities?.[activeIndex]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdvancedNavigation && setAdvancedNavigation(activities?.length > 8);
  }, []);

  const isCardActive = (index: number) => index === activeIndex;
  // if card index is above the active index, the card must be shown
  //const isCardVisible = (index: number) => index >= activeIndex; // nascondo le card precedenti a quella attiva
  const isCardVisible = (index: number) => {
    if (!advancedNavigation) return true;
    if (isCardActive(index)) return true;
    return index >= indexFirstDot && index < indexFirstDot + maxActivitiesVisible;
  };

  return (
    <NavigationWrapper>
      <Box
        position={ "absolute" }
        top={ advancedNavigation ? "15rem" : "unset" }
      >
        <DotColumn 
          tabIndex={ -1 } 
          role="tablist"
        >
          { dots()?.map((dot) => (
            <Stack
              key={ dot.index }
              tabIndex={ -1 }
              aria-selected={ isCardActive(dot.index) }
              role="tab"
              onClick={ () => dot.onClick() }
              sx={ {
                alignItems: "center",
                cursor: "pointer",
                height: "1.25rem",
                justifyContent: "center",
                width: "1.5rem"
              } }
            >
              <DotButton
                tabIndex={ -1 }
                $active={ isCardActive(dot.index) }
                $isComplete={ activities[dot.index].status === "C" }
                $isNavigationDot={ dot.small ?? null }
              />
            </Stack>
          )) }
        </DotColumn>

      </Box>

      <CardsColumn
        id={ `cards-column-${courseId}` }
        marginLeft={ "2.5rem" }
      >
        { activities?.map((activity, index) => (
          <CardSlot
            key={ index }
            className="cardSlot"

            sx={ {
              ...isCardVisible(index) && {
                gridTemplateRows: "1fr",
                opacity: 1,
                pointerEvents: "auto"
              }
            } }
          >
            <CardSlotInner
              className="cardSlotInner"
            >
              <CourseActivityCard
                { ...activity }
                activity={ activity }
                index={ index + 1 }
                realIndex={ index }
                isActive={ isCardActive(index) }
                handleActivityClick={ (i) => handleDotClick(i as number) }
                madeAttempts={ undefined }
              />
            </CardSlotInner>
          </CardSlot>
        )) }
      </CardsColumn>
    </NavigationWrapper>
  );
}
